/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description patient info schema
 */

import { SelectProps } from 'antd';
import { disableFutureDates } from 'utils/eligibility/eligibilityRequestForm';
import {
	FIELDS_META,
	AUTH_TO_RELEASE_MEDICAL_INFORMATION_OPTIONS,
	BENEFIT_ASSIGNMENT_CERTIFICATION_OPTIONS,
	PROVIDER_ACCEPT_ASSIGNMENT_OPTIONS,
} from 'app-constants/patients/insurance';
import insuranceTypeCodes from 'app-constants/patients/insuranceTypeCodes.json';
import relationshipOptions from 'app-constants/patientRelationship.json';
import { PAYER_RESPONSIBILITY_OPTIONS } from 'app-constants/claims/claimRequest';
import { YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';
import { Form } from '@formily/core';

// default select props
const defaultSelectProps: SelectProps = {
	placeholder: 'Select',
	allowClear: true,
	showSearch: true,
	optionFilterProp: 'label',
	dropdownClassName: 'tw-dropdown-option-wrap',
};

// schema keys
const schemaKeys: Record<keyof Insurance.IFormModel, keyof Insurance.IFormModel> = {
	responsibilitySequence: 'responsibilitySequence',
	payer: 'payer',
	memberId: 'memberId',
	insuranceType: 'insuranceType',
	subscriberName: 'subscriberName',
	subscriberAddress: 'subscriberAddress',
	subscriberDateOfBirth: 'subscriberDateOfBirth',
	relationshipToSubscriber: 'relationshipToSubscriber',
	groupNumber: 'groupNumber',
	planName: 'planName',
	coverageStartDate: 'coverageStartDate',
	coverageEndDate: 'coverageEndDate',
	guarantorName: 'guarantorName',
	relationshipToGuarantor: 'relationshipToGuarantor',
	providerAcceptAssignment: 'providerAcceptAssignment',
	assignmentOfBenefitAuthorized: 'assignmentOfBenefitAuthorized',
	authorizationToReleaseMedicalInformation: 'authorizationToReleaseMedicalInformation',
	remarks: 'remarks',
};

// get insurance schema
const getInsuranceSchema = (tenantPayers: Insurance.ITenantPayerOption[], isEdit: boolean): AnyRecord => {
	return {
		type: 'object',
		properties: {
			formWrapper: {
				type: 'void',
				'x-component': 'div',
				'x-component-props': {
					className: 'py-4 px-6',
				},
				properties: {
					[schemaKeys.payer]: {
						type: 'string',
						title: FIELDS_META.payer.title,
						'x-component': 'Select',
						'x-component-props': {
							...defaultSelectProps,
							disabled: isEdit,
							options: tenantPayers,
							placeholder: FIELDS_META.payer.placeholder,
						},
						'x-decorator': 'FormItem',
						'x-validator': [
							{
								required: true,
								message: FIELDS_META.payer.errorMessage,
							},
						],
					},
					gridWrapper1: {
						type: 'void',
						'x-component': 'FormGrid',
						'x-component-props': {
							minColumns: 2,
							maxColumns: 2,
						},
						properties: {
							[schemaKeys.memberId]: {
								type: 'string',
								title: FIELDS_META.memberId.title,
								'x-component': 'Input',
								'x-component-props': {
									placeholder: FIELDS_META.memberId.placeholder,
								},
								'x-decorator': 'FormItem',
								'x-validator': [
									{
										required: true,
										message: FIELDS_META.memberId.errorMessage,
									},
								],
							},
							[schemaKeys.subscriberName]: {
								type: 'string',
								title: FIELDS_META.subscriberName.title,
								'x-component': 'Input',
								'x-component-props': {
									placeholder: FIELDS_META.subscriberName.placeholder,
								},
								'x-decorator': 'FormItem',
								'x-validator': [
									{
										required: true,
										message: FIELDS_META.subscriberName.errorMessage,
									},
								],
							},
							[schemaKeys.subscriberDateOfBirth]: {
								type: 'string',
								title: FIELDS_META.subscriberDateOfBirth.title,
								'x-component': 'DatePicker',
								'x-component-props': {
									placeholder: FIELDS_META.subscriberDateOfBirth.placeholder,
									disabledDate: disableFutureDates,
									inputDateFormat: YEAR_MONTH_DATE_FORMAT,
									allowClear: true,
								},
								'x-decorator': 'FormItem',
								'x-validator': [
									{
										required: true,
										message: FIELDS_META.subscriberDateOfBirth.errorMessage,
									},
								],
							},
							[schemaKeys.relationshipToSubscriber]: {
								type: 'string',
								title: FIELDS_META.relationshipToSubscriber.title,
								'x-component': 'Select',
								'x-component-props': {
									...defaultSelectProps,
									options: relationshipOptions,
									allowClear: false,
									placeholder: FIELDS_META.relationshipToSubscriber.placeholder,
								},
								'x-decorator': 'FormItem',
								'x-validator': [{ required: true, message: FIELDS_META.relationshipToSubscriber.errorMessage }],
							},
							[schemaKeys.subscriberAddress]: {
								type: 'string',
								title: FIELDS_META.subscriberAddress.title,
								'x-component': 'Input',
								'x-component-props': {
									placeholder: FIELDS_META.subscriberAddress.placeholder,
								},
								'x-decorator': 'FormItem',
							},
							[schemaKeys.groupNumber]: {
								type: 'string',
								title: FIELDS_META.groupNumber.title,
								'x-component': 'Input',
								'x-component-props': {
									placeholder: FIELDS_META.groupNumber.placeholder,
								},
								'x-decorator': 'FormItem',
							},
						},
					},
					gridWrapper2: {
						type: 'void',
						'x-component': 'FormGrid',
						'x-component-props': {
							minColumns: 2,
							maxColumns: 2,
						},
						properties: {
							[schemaKeys.insuranceType]: {
								type: 'string',
								title: FIELDS_META.insuranceType.title,
								'x-component': 'Select',
								'x-component-props': {
									...defaultSelectProps,
									options: insuranceTypeCodes,
									placeholder: FIELDS_META.insuranceType.placeholder,
								},
								'x-decorator': 'FormItem',
							},
							[schemaKeys.responsibilitySequence]: {
								type: 'string',
								title: FIELDS_META.responsibilitySequence.title,
								'x-component': 'Select',
								'x-component-props': {
									...defaultSelectProps,
									options: PAYER_RESPONSIBILITY_OPTIONS,
									placeholder: FIELDS_META.responsibilitySequence.placeholder,
								},
								'x-decorator': 'FormItem',
							},
						},
					},
					duplicateResponsibilityAlert: {
						type: 'void',
						'x-component': 'div',
						'x-component-props': {
							children: null,
						},
					},
					gridWrapper3: {
						type: 'void',
						'x-component': 'FormGrid',
						'x-component-props': {
							minColumns: 2,
							maxColumns: 2,
						},
						properties: {
							[schemaKeys.planName]: {
								type: 'string',
								title: FIELDS_META.planName.title,
								'x-component': 'Input',
								'x-component-props': {
									placeholder: FIELDS_META.planName.placeholder,
								},
								'x-decorator': 'FormItem',
							},
							[schemaKeys.coverageStartDate]: {
								type: 'string',
								title: FIELDS_META.coverageStartDate.title,
								'x-component': 'DatePicker',
								'x-component-props': {
									placeholder: FIELDS_META.coverageStartDate.placeholder,
									inputDateFormat: YEAR_MONTH_DATE_FORMAT,
									allowClear: true,
								},
								'x-validator': [
									{
										message: 'Coverage start date should be smaller than coverage end date',
										validator: (
											value: string,
											{ message }: Record<'message', string>,
											context: AnyType,
										): string | null => {
											const formValues = context?.form?.values as Insurance.IFormModel;
											(context?.form as Form)?.setFieldState(schemaKeys.coverageEndDate, { feedbacks: [] });
											if (!!value && formValues?.coverageEndDate) {
												const isValid = value <= formValues?.coverageEndDate;
												return !isValid ? message : null;
											}
											return null;
										},
									},
								],
								'x-decorator': 'FormItem',
							},
							[schemaKeys.coverageEndDate]: {
								type: 'string',
								title: FIELDS_META.coverageEndDate.title,
								'x-component': 'DatePicker',
								'x-component-props': {
									placeholder: FIELDS_META.coverageEndDate.placeholder,
									inputDateFormat: YEAR_MONTH_DATE_FORMAT,
									allowClear: true,
								},
								'x-decorator': 'FormItem',
								'x-validator': [
									{
										message: 'Coverage end date should be greater than coverage start date',
										validator: (
											value: string,
											{ message }: Record<'message', string>,
											context: AnyType,
										): string | null => {
											const formValues = context?.form?.values as Insurance.IFormModel;
											(context?.form as Form)?.setFieldState(schemaKeys.coverageStartDate, { feedbacks: [] });

											if (!!value && formValues?.coverageStartDate) {
												const isValid = value >= formValues?.coverageStartDate;
												return !isValid ? message : null;
											}
											return null;
										},
									},
								],
							},
							[schemaKeys.guarantorName]: {
								type: 'string',
								title: FIELDS_META.guarantorName.title,
								'x-component': 'Input',
								'x-component-props': {
									placeholder: FIELDS_META.guarantorName.placeholder,
								},
								'x-decorator': 'FormItem',
							},
							[schemaKeys.relationshipToGuarantor]: {
								type: 'string',
								title: FIELDS_META.relationshipToGuarantor.title,
								'x-component': 'Select',
								'x-component-props': {
									...defaultSelectProps,
									options: relationshipOptions,
									placeholder: FIELDS_META.relationshipToGuarantor.placeholder,
								},
								'x-decorator': 'FormItem',
							},
							[schemaKeys.providerAcceptAssignment]: {
								type: 'string',
								title: FIELDS_META.providerAcceptAssignment.title,
								'x-component': 'Select',
								'x-component-props': {
									...defaultSelectProps,
									options: PROVIDER_ACCEPT_ASSIGNMENT_OPTIONS,
									placeholder: FIELDS_META.providerAcceptAssignment.placeholder,
								},
								'x-decorator': 'FormItem',
							},
							[schemaKeys.assignmentOfBenefitAuthorized]: {
								type: 'string',
								title: FIELDS_META.assignmentOfBenefitAuthorized.title,
								'x-component': 'Select',
								'x-component-props': {
									...defaultSelectProps,
									options: BENEFIT_ASSIGNMENT_CERTIFICATION_OPTIONS,
									placeholder: FIELDS_META.assignmentOfBenefitAuthorized.placeholder,
								},
								'x-decorator': 'FormItem',
							},
							[schemaKeys.authorizationToReleaseMedicalInformation]: {
								type: 'string',
								title: FIELDS_META.authorizationToReleaseMedicalInformation.title,
								'x-component': 'Select',
								'x-component-props': {
									...defaultSelectProps,
									options: AUTH_TO_RELEASE_MEDICAL_INFORMATION_OPTIONS,
									placeholder: FIELDS_META.authorizationToReleaseMedicalInformation.placeholder,
								},
								'x-decorator': 'FormItem',
							},
						},
					},
					[schemaKeys.remarks]: {
						type: 'string',
						title: FIELDS_META.remarks.title,
						'x-component': 'Input.TextArea',
						'x-component-props': {
							placeholder: FIELDS_META.remarks.placeholder,
							rows: 2,
						},
						'x-decorator': 'FormItem',
						'x-decorator-props': {
							className: 'mb-0',
						},
					},
				},
			},
		},
	};
};

export { schemaKeys, defaultSelectProps };
export default getInsuranceSchema;
